import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export type QuoteReadOnlyOverride = 'on' | 'off' | null;

const KEY_PREFIX = 'haist:quote-readonly-override:';
const keyFor = (shopId: number | null | undefined) => `${KEY_PREFIX}${shopId ?? 0}`;

function readOverride(shopId: number | null | undefined): QuoteReadOnlyOverride {
  const raw = localStorage.getItem(keyFor(shopId));
  return raw === 'on' || raw === 'off' ? raw : null;
}

/**
 * Quote read-only effective state.
 *
 * - The user role `quote_read_only` is the default across all their devices.
 * - A per-device localStorage override can force either direction on this
 *   browser only ('on' = read-only, 'off' = editable). Override wins when
 *   set; otherwise fall back to the user role.
 * - Backend is NOT enforced — this is a UX guard. The override goes both
 *   directions specifically so a user with the role can still edit on
 *   their own laptop.
 */
export function useQuoteReadOnly(): {
  readOnly: boolean;
  userDefault: boolean;
  override: QuoteReadOnlyOverride;
  setOverride: (v: QuoteReadOnlyOverride) => void;
} {
  const { user } = useAuth();
  const shopId = user?.shop_id ?? null;
  const userDefault = !!user?.roles?.includes('quote_read_only');

  const [override, setOverrideState] = useState<QuoteReadOnlyOverride>(() => readOverride(shopId));

  // Refresh when the active shop changes (multi-shop accounts).
  useEffect(() => {
    setOverrideState(readOverride(shopId));
  }, [shopId]);

  // Sync across tabs / other components on the same page.
  useEffect(() => {
    const k = keyFor(shopId);
    const onStorage = (e: StorageEvent) => {
      if (e.key === k) setOverrideState(readOverride(shopId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [shopId]);

  const setOverride = useCallback(
    (v: QuoteReadOnlyOverride) => {
      const k = keyFor(shopId);
      if (v === null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
      setOverrideState(v);
      // 'storage' events don't fire on the same window — broadcast manually
      // so other components using this hook update.
      window.dispatchEvent(new StorageEvent('storage', { key: k, newValue: v ?? null }));
    },
    [shopId],
  );

  const readOnly = override !== null ? override === 'on' : userDefault;
  return { readOnly, userDefault, override, setOverride };
}
